import {
  take,
  takeLatest,
  call,
  fork,
  cancel,
  cancelled,
  put,
} from "redux-saga/effects";
import ky from "ky-universal";
import { stringify } from "query-string";
import Cookies from "js-cookie";
import { setUser, setTag, captureException } from "@sentry/react";

import { chocomemberHost, chocotvWeb } from "../../config/api";
import { AUTH_STATE_LOGIN, AUTH_STATE_LOGOUT } from "../../constants/state";
import { COOKIE_LOG_TRACKING_CLIENT_ID } from "../../constants/cookie";

import {
  LOGOUT,
  AUTH_FLOW_BEGIN,
  AUTH_FLOW_END,
  FETCH_PROFILE_REQUESTING,
  FETCH_PROFILE_ABORTED,
  FETCH_PROFILE_FAILED,
  FETCH_PROFILE_SUCCEEDED,
  REFRESH_PROFILE,
} from "../constants/actionTypes";

import authUtils from "../utils/auth";

/**
 * @param {boolean} [force=false] - ignore local cache
 */
export function* fetchProfile(force = false) {
  const abortController = new AbortController();

  try {
    const accessToken = authUtils.getAccessToken();

    if (!accessToken) {
      throw new Error("accessToken absence");
    }

    let profile = authUtils.getProfile();

    if (force || !profile?.accountId) {
      const res = yield ky.get(`${chocomemberHost}/account/profile`, {
        signal: abortController.signal,
        headers: {
          Authorization: accessToken,
        },
        searchParams: stringify({
          appId: chocotvWeb.chocomemberAppId,
        }),
      });

      const {
        AccountId,
        isAppNoAds,
        subscription,
        ownerships,
        ...restFields
      } = yield res.json();

      profile = {
        ...restFields,
        accountId: AccountId,
        isVip: isAppNoAds,
      };

      authUtils.setProfile(JSON.stringify(profile));
      authUtils.setChocomemberId(AccountId);
    }

    yield put({ type: FETCH_PROFILE_SUCCEEDED, payload: profile });

    // Identify Sentry Users
    setUser({
      id: profile.accountId,
      username: profile.name,
      email: profile.email,
      vip: profile.isVip,
    });
    setTag("user.vip", profile.isVip);
  } finally {
    if (yield cancelled()) {
      yield put({ type: FETCH_PROFILE_ABORTED });
      abortController.abort();
    }
  }
}

/**
 * @param {AbortSignal} [signal]
 */
function* refreshToken(signal) {
  const refreshToken = authUtils.getRefreshToken();

  if (!refreshToken) {
    throw new Error("refreshToken absence");
  }

  const res = yield ky.post(`${chocomemberHost}/auth/token/refresh`, {
    signal,
    headers: {
      Authorization: refreshToken,
    },
    json: {
      appId: chocotvWeb.chocomemberAppId,
      adId: yield authUtils.getClientId(),
      instanceId: Cookies.get(COOKIE_LOG_TRACKING_CLIENT_ID),
    },
  });

  const { access_token, refresh_token } = yield res.json();
  authUtils.setToken(access_token, refresh_token);
}

function* authenticate() {
  setTag("instance.id", Cookies.get(COOKIE_LOG_TRACKING_CLIENT_ID));

  if (authUtils.getAccessToken()) {
    try {
      yield put({ type: FETCH_PROFILE_REQUESTING });
      yield call(fetchProfile);
      yield put({ type: AUTH_FLOW_END, payload: AUTH_STATE_LOGIN });
    } catch {
      try {
        yield call(refreshToken);
        yield call(fetchProfile);
        yield put({ type: AUTH_FLOW_END, payload: AUTH_STATE_LOGIN });
      } catch (error) {
        yield call(authUtils.logout);
        yield put({ type: FETCH_PROFILE_FAILED, payload: error, error: true });
        yield put({ type: AUTH_FLOW_END, payload: AUTH_STATE_LOGOUT });
        // captureException(error);
      }
    }
  } else {
    yield put({ type: AUTH_FLOW_END, payload: AUTH_STATE_LOGOUT });
  }
}

export function* authFlow() {
  while (true) {
    yield take(AUTH_FLOW_BEGIN);
    yield fork(authenticate);
    const { payload } = yield take(AUTH_FLOW_END);

    if (payload === AUTH_STATE_LOGIN) {
      yield take(LOGOUT);
    }
  }
}

export function* refreshProfileFlow() {
  while (true) {
    const { payload } = yield take(AUTH_FLOW_END);

    if (payload === AUTH_STATE_LOGIN) {
      const task = yield takeLatest(REFRESH_PROFILE, fetchProfile, true);
      yield take(LOGOUT);
      yield cancel(task);
    }
  }
}
